class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-load-more]')) { event.preventDefault(); this.loadMoreProducts(); }
    });
    this.input.addEventListener('input', () => {
      this.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.input.setAttribute('aria-activedescendant', '');
      if (!this.getQuery()) {
        this.abortController.abort();
        this.productPageController?.abort();
        this.productObserver?.disconnect();
        this.searchTerm = '';
        this.close(true);
        this.toggleResetButton();
      }
    });
    window.addEventListener('resize', () => { if (this.isOpen) this.open(); });
    this.addEventListener('touchstart', (event) => {
      if (event.target.closest('.predictive-search__results-groups-wrapper')) {
        this.searchTouchY = event.touches[0]?.clientY;
      } else this.searchTouchY = null;
    }, { passive: true });
    this.addEventListener('touchmove', (event) => {
      if (window.innerWidth >= 990 || this.searchTouchY == null) return;
      if (Math.abs((event.touches[0]?.clientY || 0) - this.searchTouchY) > 10) {
        if (document.activeElement === this.input) this.input.blur();
        this.searchTouchY = null;
      }
    }, { passive: true });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  shouldResetForm() {
    return true;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (this.shouldResetForm()) {
      this.searchTerm = '';
      this.productPageController?.abort();
      this.productObserver?.disconnect();
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    // Scrolling/dismissing the phone keyboard must not close an open search modal.
    if (window.innerWidth < 990 && this.closest('.header__search')?.querySelector('details[open]')) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;

    }
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.code === 'Enter' && this.getAttribute('open') && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
      return;
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm && currentButtonText.includes(previousTerm)) {
      if (currentButtonText.split(previousTerm).length > 2) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    activeElement.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.productPageController?.abort();
    this.productObserver?.disconnect();
    this.productPageController = new AbortController();
    this.productPageQuery = searchTerm;
    this.firstProductPage = this.fetchProductPage(1, searchTerm, this.productPageController.signal);
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestController = this.abortController;
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`, {
      signal: requestController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestController.signal.aborted || this.getQuery().toLowerCase() !== queryKey) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          const cacheKeys = Object.keys(predictiveSearchInstance.cachedResults);
          if (cacheKeys.length > 20) delete predictiveSearchInstance.cachedResults[cacheKeys[0]];
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (requestController.signal.aborted || error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
      });
  }

  async fetchProductPage(page, term, signal) {
    try {
      const url = new URL(this.input.form.action, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('section_id', 'beyond-search-products');
      url.searchParams.set('page', page);
      const response = await fetch(url, { signal });
      if (!response.ok) return null;
      const html = await response.text();
      if (signal.aborted) return null;
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-product-page]');
    } catch (error) { return null; }
  }

  async initializeProductPages() {
    const controller = this.productPageController;
    const term = this.productPageQuery;
    const page = await this.firstProductPage;
    if (!page || controller?.signal.aborted || this.getQuery() !== term || !this.isOpen) return;
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;
    list.innerHTML = page.querySelector('[data-search-page-items]').innerHTML;
    const scrollArea = this.querySelector('.predictive-search__results-groups-wrapper');
    if (scrollArea) scrollArea.scrollTop = 0;
    this.currentProductPage = 1;
    this.hasMoreProducts = Boolean(page.querySelector('[data-search-next]'));
    this.productsLoading = false;
    this.querySelector('[data-search-load-more-wrap]')?.remove();
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-search-load-more-wrap', '');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'beyond-search-load-more';
    button.setAttribute('data-search-load-more', '');
    button.textContent = this.getAttribute('data-load-more-text') || 'Load more products';
    wrapper.append(button);
    list.after(wrapper);
    this.updateProductPagination();
    this.open();
    if ('IntersectionObserver' in window) {
      this.productObserver?.disconnect();
      this.productObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMoreProducts();
      }, { root: this.querySelector('.predictive-search__results-groups-wrapper'), rootMargin: '160px' });
      if (this.hasMoreProducts) this.productObserver.observe(button);
    }
  }

  updateProductPagination() {
    const button = this.querySelector('[data-search-load-more]');
    if (!button) return;
    button.hidden = !this.hasMoreProducts;
    button.disabled = this.productsLoading;
    button.textContent = this.productsLoading ? this.loadingText : (this.getAttribute('data-load-more-text') || 'Load more products');
    if (!this.hasMoreProducts) this.productObserver?.disconnect();
  }

  async loadMoreProducts() {
    if (!this.hasMoreProducts || this.productsLoading) return;
    const controller = this.productPageController;
    const term = this.productPageQuery;
    this.productsLoading = true;
    this.updateProductPagination();
    const page = await this.fetchProductPage(this.currentProductPage + 1, term, controller.signal);
    if (controller.signal.aborted || this.getQuery() !== term) return;
    this.productsLoading = false;
    if (page) {
      const list = this.querySelector('#predictive-search-results-products-list');
      list.insertAdjacentHTML('beforeend', page.querySelector('[data-search-page-items]').innerHTML);
      this.currentProductPage += 1;
      this.hasMoreProducts = Boolean(page.querySelector('[data-search-next]'));
      this.setLiveRegionText(this.querySelector('#predictive-search-products').textContent + ': ' + list.children.length);
    }
    this.updateProductPagination();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    if (window.matchMedia('(max-width: 989px)').matches) {
      template.content.querySelector('.predictive-search__search-for-button')?.remove();
    }
    this.predictiveSearchResults.replaceChildren(template.content);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.initializeProductPages();
    this.loadRelatedCollections();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  async loadRelatedCollections() {
    if (window.innerWidth < 990) return;
    const shortcuts = this.querySelector('[data-search-collection-shortcuts]');
    if (!shortcuts || shortcuts.children.length) return;
    const term = this.getQuery();
    const words = term.toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ');
    const categories = [
      ['hoodies', ['hood', 'hoods', 'hoodie', 'hoodies']],
      ['t-shirts', ['tee', 'tees', 'tshirt', 'tshirts']],
      ['jeans', ['jeans', 'denim']], ['pants', ['pants', 'trousers']],
      ['shorts', ['shorts']], ['jackets', ['jacket', 'jackets']],
      ['caps', ['cap', 'caps', 'hat', 'hats']], ['beanies', ['beanie', 'beanies']],
      ['socks', ['sock', 'socks']], ['footwear', ['shoe', 'shoes', 'sneaker', 'sneakers']]
    ];
    const match = categories.find(([, aliases]) => aliases.some(word => words.includes(word)));
    if (!match) return;
    const categoryTerm = match[0] + (words.includes('sale') ? ' sale' : '');
    this.collectionShortcutCache ||= {};
    const controller = this.abortController;
    try {
      let markup = this.collectionShortcutCache[categoryTerm];
      if (markup === undefined) {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(categoryTerm)}&resources[type]=collection&resources[limit]=8&section_id=predictive-search`, {signal:controller.signal});
        if (!response.ok) return;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        markup = parsed.querySelector('[data-search-collection-shortcuts]')?.innerHTML || '';
        this.collectionShortcutCache[categoryTerm] = markup;
      }
      if (controller.signal.aborted || this.getQuery() !== term || !this.isOpen || !shortcuts.isConnected) return;
      shortcuts.innerHTML = markup;
      this.fitCollectionShortcuts();
    } catch (error) { /* Product results and full search remain available. */ }
  }

  fitCollectionShortcuts() {
    const shortcuts = this.querySelector('[data-search-collection-shortcuts]');
    if (!shortcuts || window.innerWidth < 990) return;
    const footer = shortcuts.parentElement;
    const primary = footer.querySelector(':scope > .predictive-search__item--term');
    const available = Math.max(0, footer.clientWidth - (primary?.getBoundingClientRect().width || 220) - 16);
    const items = [...shortcuts.children];
    items.forEach(item => { item.hidden = false; });
    let used = 0;
    items.forEach(item => {
      const width = item.getBoundingClientRect().width + (used ? (parseFloat(getComputedStyle(shortcuts).columnGap) || 0) : 0);
      if (used + width > available) item.hidden = true;
      else used += width;
    });
    shortcuts.setAttribute('data-ready', '');
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    const resultsTop = this.input.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(120, Math.floor(viewportBottom - resultsTop - 48));
    return this.resultsMaxHeight;
  }

  open() {
    requestAnimationFrame(() => this.fitCollectionShortcuts());
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
